import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { UserFavoriteWithArche } from '@/lib/api';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import { archePath } from '@/lib/arche-url';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { useNotificationSummary } from '@/hooks/useNotificationSummary';

interface FavoriteCardProps {
  favorite: UserFavoriteWithArche;
  /** Rearrange mode: the card wobbles and a click no longer navigates. */
  arranging: boolean;
}

export function FavoriteCard({ favorite, arranging }: FavoriteCardProps) {
  const navigate = useNavigate();
  const { badges } = useNotificationSummary();
  const badge = badges[favorite.archId];
  // The sortable stays ENABLED outside rearrange mode, and the press-and-hold
  // that opens the mode is the dnd-kit sensor's own delay activation constraint
  // (see FavoritesGrid). Disabling it here bound no listeners at all, which meant
  // (a) the hold entered the mode but the drag never started — you had to release
  // and press again — and (b) dnd-kit's window-level `dragstart` preventDefault,
  // which it only installs once a sensor is live, was never installed, so the
  // browser's native image drag took the gesture and painted the no-drop cursor.
  // Guarding against a stray click displacing an icon is the delay constraint's
  // job, not the disabled flag's.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isSorting } =
    useSortable({
      id: favorite.archId,
      // No post-drop layout animation. The drag preview
      // (`worktopSortingStrategy`) has already slid every icon into the cell it
      // is about to occupy, so dnd-kit's FLIP would measure the icon's PRE-drop
      // cell, snap it back there and slide it in a second time — an animation
      // from the wrong place, for icons that are already home.
      animateLayoutChanges: () => false,
    });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    // Transition only WHILE sorting, so the preview slides but the drop is
    // instant. Keeping it past the drop animates the transform back to zero
    // after the icon has already been re-parented into its new cell — i.e. it
    // starts a cell-width away and slides in, the other half of the same
    // wrong-place animation.
    transition: isSorting ? transition : undefined,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      // `worktop-icon` suppresses the mobile press-and-hold furniture: iOS pops the
      // image callout sheet with a magnified preview, and both platforms would
      // otherwise select the label text mid-gesture. A hold on a home-screen icon
      // means "rearrange", so none of that native affordance applies here.
      className={`worktop-icon group relative flex flex-col items-center touch-none select-none ${
        arranging ? 'cursor-grab' : 'cursor-pointer'
      }`}
      // Android/desktop long-press and right-press raise the context menu over the
      // icon. dnd-kit suppresses it for the duration of a live drag; the drag only
      // starts after the hold, so the menu would fire first without this.
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        // dnd-kit swallows the click that ends a real drag (a capture-phase
        // stopPropagation on document), so this only guards taps made while the
        // grid is already in rearrange mode — where a tap does nothing, as on a
        // phone home screen.
        if (arranging || isDragging) return;
        navigate(`/a/${archePath({ archId: favorite.archId, slug: favorite.archeSlug })}`);
      }}
    >
      {/* The wobble lives on an INNER wrapper: a CSS animation outranks an inline
          style, so animating the sortable node's own `transform` would fight
          dnd-kit's drag/shift transforms. `animationDelay` desynchronises the
          icons so the grid doesn't pulse in lockstep. */}
      <div
        className={`flex w-full flex-col items-center ${
          arranging ? 'worktop-jiggle animate-worktop-jiggle' : ''
        }`}
        style={
          arranging ? { animationDelay: `${(hashDelay(favorite.archId) % 12) * 20}ms` } : undefined
        }
      >
        {/* Square icon tile with notification badge overlay */}
        <div className="relative w-full">
          <FavoriteIcon favorite={favorite} />
          <NotificationBadge
            count={badge?.count ?? 0}
            showMention={(badge?.mentionCount ?? 0) > 0}
            className="absolute -top-1.5 -right-1.5 z-10 ring-2 ring-background shadow"
          />
        </div>

        {/* Label underneath */}
        <span className="mt-2 text-xs font-medium text-foreground text-center line-clamp-2 w-full px-1">
          {favorite.archeName}
        </span>
      </div>
    </div>
  );
}

/** Cheap, stable per-icon offset so the wobble phases differ across the grid. */
function hashDelay(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Reusable icon visual — also used in DragOverlay */
export function FavoriteIcon({ favorite }: { favorite: UserFavoriteWithArche }) {
  return favorite.archeImageRef ? (
    <ArcheIconImg
      imageRef={favorite.archeImageRef}
      size={128}
      alt={favorite.archeName}
      className="w-full aspect-square rounded-2xl object-cover shadow-md"
    />
  ) : (
    <DefaultArcheIcon className="w-full aspect-square rounded-2xl shadow-md" />
  );
}
